import React from 'react'
import clsx from 'clsx'
import DOMPurify from 'dompurify'
import { Edit2 } from 'iconsax-react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { useTheme } from '@mui/material'
import { isEmpty } from '@SonarCX/components/organisms/LexicalEditor'
import { saveImages } from '../helpers'

import TrashIcon from '@SonarCX/components/icons/TrashIcon'
import MoreHorizontalIcon from '@SonarCX/components/icons/MoreHorizontalIcon'
import ArticleBreadcrumbs from '@SonarCX/components/atoms/ArticleBreadcrumbs'
import ButtonSecondary from '@SonarCX/components/atoms/ButtonSecondary'
import Switch from '@SonarCX/components/atoms/Switch2'
import ButtonMenu from '@SonarCX/components/molecules/ButtonMenu2'
import ArticleContent from '@SonarCX/components/molecules/ArticleContent'
import FavoriteButton from '@SonarCX/components/atoms/FavoriteButton'
import ArticleMobileOptions from './ArticleMobileOptions'
import PinButton from '@SonarCX/components/atoms/PinButton'

import {
  setEditing,
  saveArticle,
  deleteArticle,
  setCreating
} from '@SonarCX/slices/article'
import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import useFavorites from '@SonarCX/hooks/useFavorites'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import useStyles from './styles'

const SHOW_HISTORY = 'Show History'
const DELETE_DOCUMENT = 'Delete Article'
const ALL_MORE_OPTIONS = [
  //{ name: SHOW_HISTORY },
  { name: DELETE_DOCUMENT, Icon: TrashIcon }
]

export const MEDIA_IDS_KEY = 'article_media'

const MORE_OPTIONS = [{ name: SHOW_HISTORY }]

const ArticleView = () => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const {
    selected,
    articles,
    editing: articleEditing,
    creating,
    saving
  } = useAppSelector(state => state.article)
  const hasPermission = useAccess()
  const { setAction, setReason, setSubtext, setTitle, setV2 } = useAreYouSure()
  const { favoriteArticles } = useFavorites()

  const currentArticle = React.useMemo(
    () => articles[selected] ?? {},
    [articles, selected]
  )
  const isFavorite = React.useMemo(() => {
    if (!currentArticle.id) return false

    return favoriteArticles.some(fav => fav.item_id === currentArticle.id)
  }, [favoriteArticles, currentArticle])

  const [published, setPublished] = React.useState(
    Boolean(currentArticle.published_at)
  )
  const [articleEdit, setArticleEdit] = React.useState({
    type: 'article',
    title: '',
    subtitle: '',
    body: '<p></p>',
    tags: [],
    parent_id: null,
    published_at: null,
    archived_at: null
  })
  const isPublished = React.useMemo(
    () => currentArticle.published_at || null,
    [currentArticle]
  )
  const editing = React.useMemo(
    () => articleEditing || creating,
    [articleEditing, creating]
  )

  const classes = useStyles()

  const hasPermissions = React.useMemo(
    () => hasPermission([PERMS.ArticlesFull, PERMS.ArticlesAdmin]),
    [hasPermission]
  )

  React.useEffect(() => {
    return () => {
      dispatch(setCreating(false))
    }
  }, [dispatch])

  // Update published state when editting starts/stops
  React.useEffect(() => {
    setPublished(Boolean(currentArticle.published_at))
  }, [editing, currentArticle])

  // Reset article edit to current article values
  React.useEffect(() => {
    if (creating) {
      setArticleEdit(old => ({
        ...old,
        parent_id: currentArticle?.id
      }))
    } else {
      setArticleEdit(old => ({
        ...old,
        ...currentArticle,
        parent_id: currentArticle.parent?.id
      }))
    }
  }, [currentArticle, editing, creating])

  const handleDelete = React.useCallback(() => {
    setTitle('ARE YOU SURE?')
    setSubtext('Are you sure?')
    setV2(true)

    setReason('Deleting this article cannot be undone.')

    setAction(() => {
      const parent = currentArticle.parent?.id ?? null
      dispatch(
        deleteArticle({
          id: currentArticle.id,
          redirect: parent,
          type: currentArticle.type
        })
      )
    })
  }, [
    setTitle,
    setSubtext,
    setV2,
    setReason,
    setAction,
    currentArticle.parent,
    dispatch,
    currentArticle.id,
    currentArticle.type
  ])

  const handleSelect = React.useCallback(
    name => {
      if (name === DELETE_DOCUMENT) {
        handleDelete()
      }
    },
    [handleDelete]
  )

  const handleSave = React.useCallback(async () => {
    const { title, subtitle, parent_id, tags, archived_at, body } = articleEdit
    if (!title) {
      dispatch(
        AddErrorAlert({
          err: {
            message: 'Article title cannot be empty'
          }
        })
      )
      return
    }
    if (!subtitle) {
      dispatch(
        AddErrorAlert({
          err: {
            message: 'Article subtitle cannot be empty'
          }
        })
      )
      return
    }
    if (!body || isEmpty(body)) {
      dispatch(
        AddErrorAlert({
          err: {
            message: 'Article body cannot be empty'
          }
        })
      )
      return
    }

    // Upload the editors images
    const { content, media_ids } = await saveImages(body)

    let published_at = isPublished

    // Check if switch was hit
    if (Boolean(isPublished) !== published) {
      published_at = published ? new Date().toISOString() : null
    }

    const newVals = {
      body: DOMPurify.sanitize(content, {
        ADD_TAGS: ['iframe'],
        ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling']
      }),
      title,
      subtitle,
      published_at,
      archived_at,
      media_ids,
      parent_id,
      tags
    }

    dispatch(saveArticle(selected, newVals))
  }, [articleEdit, isPublished, published, dispatch, selected])

  const handleEdit = React.useCallback((prop, value) => {
    setArticleEdit(old => ({
      ...old,
      [prop]: value
    }))
  }, [])

  const handleCancelEdit = React.useCallback(() => {
    if (creating) {
      dispatch(setCreating(false))
      return
    }

    dispatch(setEditing(false))
  }, [dispatch, creating])

  const handleSaveClick = React.useCallback(() => {
    if (editing) {
      handleSave()
      return
    }

    dispatch(setEditing(true))
  }, [editing, handleSave, dispatch])

  const handleChangePublish = React.useCallback(
    newVal => {
      setTitle('ARE YOU SURE?')
      setSubtext('Are you sure?')
      setV2(true)

      setReason(
        newVal
          ? 'Publishing this on your website will make this content visible to the public.'
          : 'Unpublishing will remove this content from your website.'
      )

      setAction(() => {
        setPublished(newVal)

        if (editing) {
          return
        }

        // Instantly save new published status when not in edit mode
        dispatch(
          saveArticle(selected, {
            published_at: newVal ? new Date().toISOString() : null,
            parent_id: articleEdit.parent_id
          })
        )
      })
    },
    [
      setTitle,
      setSubtext,
      setV2,
      setReason,
      setAction,
      editing,
      dispatch,
      selected,
      articleEdit.parent_id
    ]
  )

  return (
    <div className={classes.root}>
      <ArticleBreadcrumbs className={classes.crumbs} />

      <div className={classes.titleBar}>
        <div className={classes.titleSection}>
          <div className={classes.title}>
            {creating
              ? 'Create A New Article'
              : currentArticle.title ?? 'Unknown'}
          </div>

          {!creating && (
            <div
              className={clsx(
                classes.statusBox,
                isPublished ? classes.published : classes.draft
              )}
            >
              {isPublished ? 'Published' : 'Draft'}
            </div>
          )}

          <div className={classes.mobileOptions}>
            <ArticleMobileOptions
              editing={editing}
              published={published}
              isFavorite={isFavorite}
              articleID={currentArticle.id}
              handleCancelEdit={handleCancelEdit}
              handleSaveClick={handleSaveClick}
              handleChangePublish={handleChangePublish}
              handleDelete={handleDelete}
            />
          </div>
        </div>

        <div className={classes.buttonArea}>
          {editing && (
            <ButtonSecondary
              onClick={handleCancelEdit}
              className={classes.editButton}
            >
              Cancel
            </ButtonSecondary>
          )}

          {hasPermissions && (
            <>
              <ButtonSecondary
                onClick={handleSaveClick}
                disabled={saving}
                className={classes.editButton}
              >
                {!editing && (
                  <Edit2
                    size={theme.size.base}
                    variant='Bold'
                    className={classes.editIcon}
                  />
                )}

                {editing ? `${published ? 'Save' : 'Save as draft'}` : 'Edit'}
              </ButtonSecondary>

              <Switch
                checked={published}
                onChange={newVal => handleChangePublish(newVal)}
                className={classes.publish}
              >
                {published ? 'Unpublish' : 'Publish on Website'}
              </Switch>
            </>
          )}

          {!creating && (
            <>
              <ButtonMenu
                options={hasPermissions ? ALL_MORE_OPTIONS : MORE_OPTIONS}
                onSelect={handleSelect}
                className={classes.menu}
                classes={{
                  title: classes.menuTitle
                }}
              >
                <MoreHorizontalIcon />
              </ButtonMenu>

              <FavoriteButton
                isFavorite={isFavorite}
                itemId={currentArticle.id}
                type='article'
              />

              <PinButton
                id={currentArticle.id}
                type='article'
                title={currentArticle.title}
              />
            </>
          )}
        </div>
      </div>

      <ArticleContent editingValues={articleEdit} onEdit={handleEdit} />
    </div>
  )
}

export default ArticleView
